import { Box, Button, Switch } from '@deckee/deck-hand';
import React, { FC, KeyboardEvent, useState } from 'react';
import { captureEvent } from '../../../../../services/event-capture';
import RangeSlider from '../../../../shared/RangeSlider/RangeSlider';

const MANUAL_WATERCRAFT_TYPES = [
  { label: 'Motorboat', value: 'motorboat' },
  { label: 'Sailboat', value: 'sailboat' },
  { label: 'PWC', value: 'personal watercraft' },
  { label: 'Paddle', value: 'paddle craft' },
] as const;

const VESSEL_LENGTH_UNIT_STEPS = {
  m: {
    unitLabel: 'meters',
    unitMultiplyer: 1,
    toggleLabel: 'feet',
    unitStepValue: 0.1,
  },
  ft: {
    unitLabel: 'feet',
    unitMultiplyer: 3.28084,
    toggleLabel: 'metres',
    unitStepValue: 0.152,
  },
} as const;

type LengthUnit = keyof typeof VESSEL_LENGTH_UNIT_STEPS;

interface RiskForecastEditVesselLengthFormProps {
  vesselLength: number;
  watercraftType?: string;
  saveAction: (newVesselLength: number, watercraftType?: string) => void;
}

const RiskForecastEditVesselLengthForm: FC<
  RiskForecastEditVesselLengthFormProps
> = ({ vesselLength, watercraftType: initialWatercraftType, saveAction }) => {
  const initialLength =
    Number.isFinite(vesselLength) && vesselLength >= 1 ? vesselLength : 4.5;
  const [lengthUnit, setLengthUnit] = useState<LengthUnit>('m');
  const [vesselLengthValue, setVesselLengthValue] = useState(initialLength);
  const unitsConfig = VESSEL_LENGTH_UNIT_STEPS[lengthUnit];
  const [watercraftType, setWatercraftType] = useState<string>(
    initialWatercraftType ?? MANUAL_WATERCRAFT_TYPES[0].value,
  );

  const roundFeetToNearestStepValue = (lengthInMeters: number) => {
    const vesselLengthInFeet =
      lengthInMeters * VESSEL_LENGTH_UNIT_STEPS.ft.unitMultiplyer;
    const roundedLengthInFeet = Math.round(vesselLengthInFeet / 0.5) * 0.5;
    return roundedLengthInFeet * 0.3048;
  };

  const switchUnits = () => {
    const nextUnit: LengthUnit = lengthUnit === 'm' ? 'ft' : 'm';
    captureEvent({
      type: 'Risk forecast edit vessel length: Switch units',
      payload: {
        unitsSwitchedTo: nextUnit === 'ft' ? 'feet' : 'meters',
        vesselLengthValue,
        vesselLengthUnit: unitsConfig.unitLabel,
      },
      sendToAmplitude: true,
    });

    if (nextUnit === 'ft') {
      setVesselLengthValue(roundFeetToNearestStepValue(vesselLengthValue));
    }

    setLengthUnit(nextUnit);
  };

  const saveVesselLength = () => {
    captureEvent({
      type: 'Risk forecast edit vessel length: Save new vessel length',
      payload: {
        vesselLengthValue,
        vesselLengthUnit: unitsConfig.unitLabel,
        watercraftType,
      },
      sendToAmplitude: true,
    });
    saveAction(vesselLengthValue, watercraftType);
  };

  return (
    <Box width="100%" data-testid="risk-forecast-manual-form">
      <Box
        mb={2}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Box fontSize={5} fontWeight={700}>
          {(vesselLengthValue * unitsConfig.unitMultiplyer).toFixed(1)}
          <Box as="span" ml={1} fontSize={3} fontWeight={400}>
            {unitsConfig.unitLabel}
          </Box>
        </Box>
        <Box
          px={4}
          pt={2}
          pb={3}
          role="link"
          color="primary"
          cursor="pointer"
          onClick={switchUnits}
          onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              switchUnits();
            }
          }}
          data-testid="risk-forecast-switch-units"
        >
          {`Switch to ${unitsConfig.toggleLabel}`}
        </Box>
      </Box>

      <RangeSlider
        value={vesselLengthValue}
        min={1}
        max={20}
        step={unitsConfig.unitStepValue}
        onChange={setVesselLengthValue}
        onChangeCommitted={(committedValue) => {
          captureEvent({
            type: 'Risk forecast edit vessel length: Adjust vessel length',
            payload: {
              vesselLengthValue: committedValue,
              vesselLengthUnit: unitsConfig.unitLabel,
            },
            sendToAmplitude: true,
          });
        }}
      />

      <Box mt={4}>
        <Switch.Group
          name="watercraft-type"
          value={watercraftType}
          onChange={(event) => {
            const newWatercraftType = event.target.value;
            setWatercraftType(newWatercraftType);
            captureEvent({
              type: 'Risk forecast edit vessel length: Select watercraft type',
              payload: { watercraftType: newWatercraftType },
              sendToAmplitude: true,
            });
          }}
        >
          {MANUAL_WATERCRAFT_TYPES.map((type) => (
            <Switch
              key={type.value}
              id={`watercraft-type-${type.value.replace(/\s+/g, '-')}`}
              value={type.value}
            >
              {type.label}
            </Switch>
          ))}
        </Switch.Group>
      </Box>

      <Button.Group mt="4" wide>
        <Button
          onClick={saveVesselLength}
          variant="primary"
          wide
          data-testid="risk-forecast-manual-done"
        >
          Done
        </Button>
      </Button.Group>
    </Box>
  );
};

export default RiskForecastEditVesselLengthForm;
